import React, { useState, useEffect } from 'react';
import { X, User, DollarSign, CreditCard, Shield, Mail, Loader } from 'lucide-react';
import { Collaborator } from '../../types/collaborators';
import { UserRole } from '../../types/roles';
import { useVersionTracking } from '../../hooks/useVersionTracking';
import { rolesService } from '../../services/rolesService';

interface CollaboratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (collaborator: Partial<Collaborator>, invite?: { roleId: string }) => void;
  collaborator?: Collaborator | null;
  onInviteToCRM?: (collaborator: Collaborator) => void;
}

const CollaboratorModal: React.FC<CollaboratorModalProps> = ({ isOpen, onClose, onSave, collaborator, onInviteToCRM }) => {
  const { trackCollaboratorAction } = useVersionTracking();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    hourlyRate: 0,
    paymentMethod: 'bank_transfer' as Collaborator['paymentMethod'],
    currency: 'EUR' as Collaborator['currency'],
    isActive: true,
  });
  const [sendInvitation, setSendInvitation] = useState(true);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        setLoadingRoles(true);
        const all = await rolesService.getAllRoles();
        const relevant = all.filter(
          (r) => r.name === 'colaborador_programador' || r.name === 'colaborador_comercial'
        );
        setRoles(relevant);
        if (!selectedRole && relevant.length > 0) {
          const programador = relevant.find((r) => r.name === 'colaborador_programador');
          setSelectedRole((programador || relevant[0]).id);
        }
      } catch (err) {
        console.error('Error cargando roles:', err);
        setRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (collaborator) {
      setFormData({
        name: collaborator.name,
        email: collaborator.email,
        country: collaborator.country,
        hourlyRate: collaborator.hourlyRate,
        paymentMethod: collaborator.paymentMethod,
        currency: collaborator.currency,
        isActive: collaborator.isActive,
      });
      setSendInvitation(false);
    } else {
      setFormData({
        name: '',
        email: '',
        country: '',
        hourlyRate: 0,
        paymentMethod: 'bank_transfer',
        currency: 'EUR',
        isActive: true,
      });
      setSendInvitation(true);
    }
  }, [collaborator]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.hourlyRate < 0) {
      alert('La tarifa por hora no puede ser negativa');
      return;
    }
    if (!formData.name.trim()) {
      alert('El nombre no puede estar vacío');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      alert('El formato del email no es válido');
      return;
    }

    // Al crear, si el usuario marcó "enviar invitación" pero no eligió rol, avisar.
    const invite = !collaborator && sendInvitation;
    if (invite && !selectedRole) {
      alert('Selecciona un rol para poder enviar la invitación');
      return;
    }

    trackCollaboratorAction(collaborator ? 'update' : 'create', formData.name);

    onSave(
      { ...formData, name: formData.name.trim(), email: formData.email.trim().toLowerCase() },
      invite ? { roleId: selectedRole } : undefined
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {collaborator ? 'Editar Colaborador' : 'Nuevo Colaborador'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Información personal */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <User className="w-5 h-5" />
              <span>Información Personal</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="ej: Juan Pérez"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="juan@freelancer.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">País *</label>
                <select
                  required
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seleccionar país</option>
                  <option value="España">España</option>
                  <option value="Argentina">Argentina</option>
                  <option value="México">México</option>
                  <option value="Colombia">Colombia</option>
                  <option value="Chile">Chile</option>
                  <option value="Perú">Perú</option>
                  <option value="Uruguay">Uruguay</option>
                  <option value="Venezuela">Venezuela</option>
                  <option value="Ecuador">Ecuador</option>
                  <option value="Bolivia">Bolivia</option>
                  <option value="Paraguay">Paraguay</option>
                  <option value="Estados Unidos">Estados Unidos</option>
                  <option value="Reino Unido">Reino Unido</option>
                  <option value="Francia">Francia</option>
                  <option value="Alemania">Alemania</option>
                  <option value="Italia">Italia</option>
                  <option value="Portugal">Portugal</option>
                  <option value="Brasil">Brasil</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                  Colaborador activo
                </label>
              </div>
            </div>
          </div>

          {/* Información de pago */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <DollarSign className="w-5 h-5" />
              <span>Información de Pago</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tarifa por hora *</label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.hourlyRate}
                  onChange={(e) => setFormData({ ...formData, hourlyRate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="45.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Moneda *</label>
                <select
                  required
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value as Collaborator['currency'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="other">Otra</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Método de pago *</label>
                <select
                  required
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as Collaborator['paymentMethod'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="bank_transfer">Transferencia Bancaria</option>
                  <option value="paypal">PayPal</option>
                  <option value="wise">Wise</option>
                  <option value="crypto">Criptomonedas</option>
                  <option value="other">Otro</option>
                </select>
              </div>
            </div>
          </div>

          {/* Acceso al CRM: al crear ⇒ selector de rol + envío inmediato; al editar ⇒ estado + botón */}
          {!collaborator ? (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <span>Acceso al CRM</span>
              </h3>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={sendInvitation}
                    onChange={(e) => setSendInvitation(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-blue-600" />
                      Enviar invitación por email al crear
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      El colaborador recibirá un enlace de activación de 7 días para definir su
                      contraseña y entrar al CRM con el rol elegido.
                    </p>
                  </div>
                </label>

                {sendInvitation && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rol en el CRM *</label>
                    {loadingRoles ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                        <Loader className="w-4 h-4 animate-spin" /> Cargando roles...
                      </div>
                    ) : (
                      <select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required={sendInvitation}
                      >
                        <option value="">Seleccionar rol</option>
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>{r.display_name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <span>Acceso al CRM</span>
              </h3>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">
                      {collaborator.has_crm_access ? 'Acceso Activo' : 'Sin Acceso al CRM'}
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      {collaborator.has_crm_access
                        ? 'Este colaborador ya tiene acceso al CRM.'
                        : 'Este colaborador aún no tiene acceso. Puedes invitarlo desde aquí.'}
                    </p>
                    {collaborator.has_crm_access && collaborator.access_granted_at && (
                      <p className="text-xs text-gray-500 mt-2">
                        Acceso otorgado: {new Date(collaborator.access_granted_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  {!collaborator.has_crm_access && onInviteToCRM && (
                    <button
                      type="button"
                      onClick={() => onInviteToCRM(collaborator)}
                      className="ml-4 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                    >
                      <Shield className="w-4 h-4" />
                      <span>Invitar al CRM</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Info tarifa */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <CreditCard className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">Información importante</h4>
                <p className="text-sm text-blue-700 mt-1">
                  La tarifa por hora se aplicará automáticamente a los partes de horas del colaborador.
                  Puedes modificarla individualmente en cada parte si es necesario.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {collaborator
                ? 'Actualizar Colaborador'
                : sendInvitation
                  ? 'Crear y enviar invitación'
                  : 'Crear Colaborador'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CollaboratorModal;
